import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const FavorisContext = createContext();

export const FavorisProvider = ({ children }) => {
  const [favoris, setFavoris] = useState([]);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  // Charger les favoris depuis localStorage au démarrage
  useEffect(() => {
    const savedFavoris = localStorage.getItem('favoris');
    if (savedFavoris) {
      setFavoris(JSON.parse(savedFavoris));
    }
  }, []);

  // Sauvegarder les favoris dans localStorage à chaque changement
  useEffect(() => {
    localStorage.setItem('favoris', JSON.stringify(favoris));
  }, [favoris]);

  // Vérifier si un produit est en favoris
  const isFavorite = (produitId) => {
    return favoris.includes(produitId);
  };

  // Ajouter/Retirer un favori
  const toggleFavorite = async (produitId) => {
    const isFav = isFavorite(produitId);
    
    if (isFav) {
      // Retirer des favoris
      setFavoris(favoris.filter(id => id !== produitId));
      
      // Si l'utilisateur est connecté, synchroniser avec le backend
      if (user) {
        try {
          await axios.delete(`/favoris/${produitId}`);
        } catch (error) {
          console.error('Erreur lors de la suppression du favori:', error);
        }
      }
      
      return { success: true, message: 'Retiré des favoris', action: 'removed' };
    } else {
      // Ajouter aux favoris
      setFavoris([...favoris, produitId]);
      
      // Si l'utilisateur est connecté, synchroniser avec le backend
      if (user) {
        try {
          await axios.post('/favoris', { produitId });
        } catch (error) {
          console.error('Erreur lors de l\'ajout du favori:', error);
        }
      }
      
      return { success: true, message: 'Ajouté aux favoris', action: 'added' };
    }
  };

  // Récupérer tous les favoris de l'utilisateur (avec détails des produits)
  const getFavoris = async () => {
    if (!user) {
      return { success: false, message: 'Veuillez vous connecter' };
    }

    setLoading(true);
    try {
      const res = await axios.get('/favoris');
      setFavoris(res.data.map(fav => fav.produit._id));
      setLoading(false);
      return { success: true, data: res.data };
    } catch (error) {
      console.error('Erreur lors de la récupération des favoris:', error);
      setLoading(false);
      return { success: false, message: error.response?.data?.message || 'Erreur serveur' };
    }
  };

  // Synchroniser les favoris locaux avec le serveur lors de la connexion
  const syncFavoris = async () => {
    if (!user || favoris.length === 0) return;

    try {
      // Envoyer tous les favoris locaux au serveur
      await Promise.all(
        favoris.map(produitId => 
          axios.post('/favoris', { produitId }).catch(() => {})
        )
      );
      // Récupérer les favoris du serveur
      await getFavoris();
    } catch (error) {
      console.error('Erreur lors de la synchronisation des favoris:', error);
    }
  };

  // Synchroniser lors de la connexion de l'utilisateur
  useEffect(() => {
    if (user) {
      syncFavoris();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return (
    <FavorisContext.Provider
      value={{
        favoris,
        loading,
        isFavorite,
        toggleFavorite,
        getFavoris,
        totalFavoris: favoris.length
      }}
    >
      {children}
    </FavorisContext.Provider>
  );
};

export const useFavoris = () => {
  const context = useContext(FavorisContext);
  if (!context) {
    throw new Error('useFavoris must be used within a FavorisProvider');
  }
  return context;
};

export default FavorisContext;
